import { NgClass } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, input } from '@angular/core';
import { ChatbotConfig } from '@shared/index';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Subscription } from 'rxjs';
import { SocketService } from '../../../../services/socket.service';
import { UserDocument, UserDocumentsService } from '../../../../services/user-documents.service';

@Component({
    selector: 'app-user-documents',
    imports: [NgClass, TooltipModule],
    templateUrl: './user-documents.component.html',
    styleUrl: './user-documents.component.scss'
})
export class UserDocumentsComponent implements OnInit, OnDestroy {

    @Input({ required: true }) public botConfig!: ChatbotConfig;
    public readonly botId = input.required<string>();

    public panelOpen = false;
    public documents: UserDocument[] = [];
    public isLoading = false;
    public uploadStatus: { status: 'uploading' | 'complete' | 'error', filename: string, subStatus: string, message?: string } | null = null;
    public allowedTypes: string[] = [];

    public get acceptString(): string {
        return this.allowedTypes.length > 0 ? this.allowedTypes.join(',') : '*';
    }

    public get allowedTypesLabel(): string {
        return this.allowedTypes.length > 0
            ? this.allowedTypes.map(t => t.replace('.', '').toUpperCase()).join(', ')
            : '';
    }

    public readonly openLabel = $localize`:@@user-docs-open:Meine Dokumente anzeigen`;
    public readonly closeLabel = $localize`:@@user-docs-close:Dokumente schließen`;
    public readonly deleteLabel = $localize`:@@user-docs-delete-doc:Dokument löschen`;

    private statusSub?: Subscription;
    public isDraggingOver = false;
    private dragCounter = 0;

    constructor(
        private userDocumentsService: UserDocumentsService,
        private socketService: SocketService,
        private elementRef: ElementRef
    ) { }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        if (this.panelOpen && !this.elementRef.nativeElement.contains(event.target)) {
            this.panelOpen = false;
        }
    }

    @HostListener('document:dragenter', ['$event'])
    public onDragEnter(event: DragEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!event.dataTransfer?.types?.includes('Files')) return;
        this.dragCounter++;
        this.isDraggingOver = true;
        event.preventDefault();
    }

    @HostListener('document:dragleave', ['$event'])
    public onDragLeave(event: DragEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!event.dataTransfer?.types?.includes('Files')) return;
        this.dragCounter--;
        if (this.dragCounter <= 0) {
            this.dragCounter = 0;
            this.isDraggingOver = false;
        }
    }

    @HostListener('document:dragover', ['$event'])
    public onDragOver(event: DragEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!event.dataTransfer?.types?.includes('Files')) return;
        event.preventDefault();
    }

    @HostListener('document:drop', ['$event'])
    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragCounter = 0;
        this.isDraggingOver = false;
        if (!this.uploadAllowed) return;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const file = event.dataTransfer?.files?.[0];
        if (file) this.processFile(file);
    }

    public ngOnInit(): void {
        void this.loadDocuments();
        void this.userDocumentsService.getAllowedTypes().then(t => { this.allowedTypes = t; }).catch(() => { });
        this.statusSub = this.socketService.onUserDocUploadStatus.subscribe((data) => {
            this.uploadStatus = data;
            if (data.status === 'complete') {
                void this.loadDocuments();
                setTimeout(() => { this.uploadStatus = null; }, 3000);
            } else if (data.status === 'error') {
                setTimeout(() => { this.uploadStatus = null; }, 5000);
            }
        });
    }

    public ngOnDestroy(): void {
        this.statusSub?.unsubscribe();
    }

    public togglePanel(): void {
        this.panelOpen = !this.panelOpen;
    }

    public async loadDocuments(): Promise<void> {
        this.isLoading = true;
        try {
            this.documents = await this.userDocumentsService.listDocuments(this.botId());
        } catch {
            // silent fail
        } finally {
            this.isLoading = false;
        }
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        input.value = '';
        this.processFile(file);
    }

    private processFile(file: File): void {
        if (file.size > this.botConfig.userDocumentsMaxMb * 1024 * 1024) {
            this.uploadStatus = {
                status: 'error',
                filename: file.name,
                subStatus: '',
                message: $localize`:@@user-docs-file-too-large:Die Datei ist zu groß. Erlaubt sind maximal ${this.botConfig.userDocumentsMaxMb} MB.`
            };
            setTimeout(() => { this.uploadStatus = null; }, 5000);
            return;
        }

        this.uploadStatus = { status: 'uploading', filename: file.name, subStatus: '' };
        if (!this.panelOpen) this.panelOpen = true;
        void this.userDocumentsService.uploadDocument(this.botId(), file).catch((_err: unknown) => {
            this.uploadStatus = {
                status: 'error',
                filename: file.name,
                subStatus: '',
                message: $localize`:@@user-docs-upload-failed:Das Hochladen ist fehlgeschlagen. Versuche es erneut.`
            };
            setTimeout(() => { this.uploadStatus = null; }, 5000);
        });
    }

    public async deleteDocument(doc: UserDocument): Promise<void> {
        try {
            await this.userDocumentsService.deleteDocument(this.botId(), doc._id);
            this.documents = this.documents.filter(d => d._id !== doc._id);
        } catch {
            // silent fail
        }
    }

    public get uploadAllowed(): boolean {
        const max = this.botConfig.userDocumentsMaxCount;
        return max === 0 || this.documents.length < max;
    }

    public get limitLabel(): string {
        return this.botConfig.userDocumentsMaxCount === 0 ? '∞' : String(this.botConfig.userDocumentsMaxCount);
    }
}
